import { ActionType, RegionId, rootRegionParentId } from "@sp-crm/core";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useIsMultiRegionUser } from "store/selectors/hooks";
import { useEntityRegions } from "store/selectors/regions";
import { ApplicationState } from "store/state";
import { TreeNode } from "./tree-node";
import {
    buildRegionTree,
    computeVisibleRegions,
    cycleVisibilityMode,
    flattenTree,
    getDescendantIds,
    getVisibilityMode,
    getVisibilitySummary,
    RegionVisibility,
} from "./tree-utils";
import { VisibilitySummary } from "./visibility-summary";

export interface RegionVisibilityTreeProps {
    actionType: ActionType;
    editable: boolean;
    regionVisibility: RegionVisibility[];
    onChange: (regions: RegionVisibility[]) => void;
    entityName: string;
}

export const RegionVisibilityTree: React.FC<RegionVisibilityTreeProps> = props => {
    const { actionType, editable, regionVisibility, onChange, entityName } = props;

    const allRegions = useSelector((state: ApplicationState) => state.region.regions);
    const tenantName = useSelector((state: ApplicationState) => state.tenant.tenant.name);
    const editableRegions = useEntityRegions({ actionType, enabled: editable });
    const editableRegionIds = useMemo(
        () => new Set(editableRegions.map(r => r.id)),
        [editableRegions],
    );

    const [expandedIds, setExpandedIds] = useState<Set<RegionId>>(() => {
        const tree = buildRegionTree(allRegions);
        const initialExpanded = new Set<RegionId>();
        tree.forEach(root => {
            initialExpanded.add(root.region.id);
            root.children.forEach(child => {
                initialExpanded.add(child.region.id);
            });
        });
        return initialExpanded;
    });

    const tree = useMemo(() => buildRegionTree(allRegions), [allRegions]);
    const flatNodes = useMemo(() => flattenTree(tree), [tree]);
    const visibleRegionsMap = useMemo(
        () => computeVisibleRegions(allRegions, regionVisibility),
        [allRegions, regionVisibility],
    );
    const summary = useMemo(
        () => getVisibilitySummary(allRegions, regionVisibility),
        [allRegions, regionVisibility],
    );

    const handleToggleExpand = useCallback((regionId: RegionId) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(regionId)) {
                next.delete(regionId);
            } else {
                next.add(regionId);
            }
            return next;
        });
    }, []);

    const handleToggleVisibility = useCallback(
        (regionId: RegionId) => {
            const node = flatNodes.find(n => n.region.id === regionId);
            if (!node) return;

            const currentMode = getVisibilityMode(
                regionVisibility,
                visibleRegionsMap,
                regionId,
            );
            const hasChildren = node.children.length > 0;
            const descendantIds = getDescendantIds(node);
            const isRootRegion = node.region.parentRegionId === rootRegionParentId;

            const newVisibility = cycleVisibilityMode(
                regionVisibility,
                regionId,
                currentMode,
                hasChildren,
                descendantIds,
                isRootRegion,
            );
            onChange(newVisibility);
        },
        [regionVisibility, visibleRegionsMap, flatNodes, onChange],
    );

    const isNodeVisible = useCallback(
        (node: { region: { id: RegionId }; depth: number }) => {
            if (node.depth === 0) return true;

            let currentNode: { region: { id: RegionId }; depth: number } = node;
            while (currentNode.depth > 0) {
                const parentNode = flatNodes.find(n =>
                    n.children.some(c => c.region.id === currentNode.region.id),
                );
                if (!parentNode) break;
                if (!expandedIds.has(parentNode.region.id)) return false;
                currentNode = parentNode;
            }
            return true;
        },
        [flatNodes, expandedIds],
    );

    const isMultiRegion = useIsMultiRegionUser();

    if (!editable || !isMultiRegion) {
        return null;
    }

    return (
        <div className="mt-4">
            <div className="border border-gray-200 rounded-md bg-white">
                <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex justify-between text-xs font-medium text-gray-500 uppercase tracking-wide">
                    <span>Office</span>
                    <span>Availability</span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                    {flatNodes.map(node => {
                        if (!isNodeVisible(node)) return null;

                        const visibilityMode = getVisibilityMode(
                            regionVisibility,
                            visibleRegionsMap,
                            node.region.id,
                        );
                        const isEditable = editableRegionIds.has(node.region.id);

                        return (
                            <TreeNode
                                key={node.region.id}
                                node={node}
                                visibilityMode={visibilityMode}
                                isExpanded={expandedIds.has(node.region.id)}
                                isEditable={isEditable}
                                tenantName={tenantName}
                                onToggleExpand={handleToggleExpand}
                                onToggleVisibility={handleToggleVisibility}
                            />
                        );
                    })}
                </div>
                <VisibilitySummary
                    totalVisible={summary.totalVisible}
                    entityName={entityName}
                    regionNames={summary.regionNames}
                />
            </div>
        </div>
    );
};
